import React from 'react';
import { Icons, IconName } from '../../../icons';
import { useTheme } from "@context/ThemeContext";
import { PrimeReactProvider } from 'primereact/api';
import { Button as PrimeButton } from 'primereact/button';
import styled from 'styled-components';

import './buttons.scss';

export interface ButtonProps {
	primary?: boolean;
	backgroundColor?: string;
	border?: string;
	borderColor?: string;
	borderRadius?: string;
	size?: "xs" | "small" | "medium" | "normal" | "large";
	label?: string;
	onClick?: () => void;
	disabled?: boolean;
	iconName?: IconName;
	onlyIcon?: boolean;
	iconPos?: "left" | "right";
	fullWidth?: boolean;
	loading?: boolean;
	loadingText?: string;
	rounded?: boolean;
	outlined?: boolean;
	text?: boolean;
	raised?: boolean;
	severity?: 'success' | 'info' | 'warning' | 'help' | 'danger' | 'secondary' | 'tertiary';
	icon?: string;
	ariaLabel?: string;
	className?: string;
	type?: 'button' | 'submit' | 'reset';
	form?: string;
}

const StyledPrimeButton = styled(PrimeButton)<{ $fullWidth?: boolean }>`
	width: ${props => props.$fullWidth ? '100%' : 'auto'};
`;

export const Button: React.FC<ButtonProps> = ({
	primary = false,
	size = "medium",
	backgroundColor,
	border,
	borderColor,
	borderRadius,
	label,
	onClick,
	disabled = false,
	iconName,
	onlyIcon = false,
	iconPos = "left",
	fullWidth = false,
	loading = false,
	loadingText = "Cargando...",
	rounded = false,
	outlined = false,
	text = false,
	raised = false,
	severity,
	icon,
	ariaLabel,
	className = '',
	type = 'button',
	form,
}) => {
	const { theme } = useTheme();
	const Icon = iconName ? Icons[iconName] : null;

	const getSeverity = () => {
		if (severity === "tertiary") return undefined;
		if (severity) return severity;
		if (primary) return undefined;
		return "secondary";
	};

	const getSize = () => {
		switch (size) {
			case 'large': return 'large';
			case 'small': return 'small';
			default: return undefined;
		}
	};

	const renderIcon = () => {
		if (icon)
			return null; // PrimeReact usará className pi pi-*
		if (Icon) {
			return (
				<Icon
					className={`custom-svg-icon icon-${iconPos}`}
					data-testid={`icon-${iconPos}`}
				/>
			);
		}
		return null;
	};

	const buttonClass = [
		"custom-button",
		className,
		`p-button-${size}`,
		severity === "tertiary" ? "p-button-tertiary" : "",
		primary ? "p-button-primary" : "",
	]
		.filter(Boolean)
		.join(" ");

	return (
		<PrimeReactProvider>
			<StyledPrimeButton
				severity={getSeverity()}
				size={getSize()}
				onClick={onClick}
				disabled={disabled || loading}
				loading={loading}
				icon={icon}
				iconPos={iconPos}
				$fullWidth={fullWidth}
				rounded={rounded}
				outlined={outlined}
				text={text || severity === 'tertiary'}
				raised={raised}
				aria-label={ariaLabel || (onlyIcon ? label : undefined)}
				style={{
					backgroundColor,
					border,
					borderColor,
					borderRadius,
				}}
				className={`
					${theme === 'dark' ? 'p-button-dark' : 'p-button-light'}
					${(onlyIcon || (onlyIcon && loading)) ? 'p-button-icon-only btn-icon-clean' : ''}
					${buttonClass}
				`}
				label={
					!onlyIcon && !iconName
					? (loading ? loadingText : label)
					: undefined
				}
				type={type}
				form={form}
				>
				{/* Solo se renderiza icono SVG si no se está usando `icon` (PrimeReact) */}
				{!icon && iconName && (
					<span className="p-button-label-with-icon">
					{iconPos === "left" && renderIcon()}
					{!onlyIcon && (loading ? loadingText : label)}
					{iconPos === "right" && renderIcon()}
					</span>
				)}
		</StyledPrimeButton>
    </PrimeReactProvider>
	);
};
